import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Mountain, Clock, ArrowLeft, Share2, Copy, Check, GitCompare } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const Section = ({ title, children, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-4 shadow-sm" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{title}</div>
    <div className="text-slate-800 text-sm leading-relaxed">{children}</div>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex flex-col sm:flex-row sm:justify-between py-1.5 border-b border-slate-100 last:border-0">
    <span className="text-xs text-slate-500 sm:w-1/3">{k}</span>
    <span className="text-slate-900 text-sm font-medium sm:text-right sm:w-2/3">{v || "—"}</span>
  </div>
);

const formatExpiry = (iso) => {
  if (!iso) return "—";
  try {
    const dt = new Date(iso);
    return dt.toLocaleString("pt-BR", { day: "2-digit", month: "long", year: "numeric",
                                          hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const renderMarkdownish = (text) => {
  const lines = String(text || "").split(/\n/);
  return (
    <div className="space-y-2 leading-relaxed">
      {lines.map((l, i) => {
        if (/^#{1,3}\s/.test(l)) {
          return <h3 key={i} className="font-heading text-base font-semibold text-slate-900 mt-4">{l.replace(/^#+\s/, "")}</h3>;
        }
        const parts = l.split(/(\*\*[^*]+\*\*)/g);
        return (
          <p key={i} className="text-sm">{parts.map((p, j) =>
            p.startsWith("**") ? <strong key={j}>{p.slice(2, -2)}</strong> : <span key={j}>{p}</span>
          )}</p>
        );
      })}
    </div>
  );
};

const AnalysisRender = ({ analysis }) => {
  if (!analysis) return <div className="text-slate-500 italic">Análise não disponível.</div>;
  if (typeof analysis === "string") return renderMarkdownish(analysis);
  if (analysis.error) return <div className="text-rose-600 text-sm">Análise indisponível: {analysis.error}</div>;
  if (typeof analysis.analysis === "string") return renderMarkdownish(analysis.analysis);
  return <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(analysis, null, 2)}</pre>;
};

export default function RelatorioMineralPublico({ shared = false }) {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [shareExp, setShareExp] = useState(null);
  const [copied, setCopied] = useState(false);
  const [creatingShare, setCreatingShare] = useState(false);

  useEffect(() => {
    const url = shared
      ? `${API}/api/public/mineral/share/${token}/order`
      : `${API}/api/public/mineral/${token}/order`;
    axios.get(url)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Falha ao carregar relatório"))
      .finally(() => setLoading(false));
  }, [token, shared]);

  const createShare = async () => {
    if (shared) return; // não pode gerar sub-link de link compartilhado
    setCreatingShare(true);
    try {
      const r = await axios.post(`${API}/api/public/mineral/${token}/create-share`, { days: 7 });
      setShareUrl(r.data.url);
      setShareExp(r.data.expires_at);
      setShareOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao criar link");
    } finally { setCreatingShare(false); }
  };

  const copyShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copiado!");
      setTimeout(() => setCopied(false), 3000);
    } catch { toast.error("Falha ao copiar"); }
  };

  const [compareOpen, setCompareOpen] = useState(false);
  const [compA, setCompA] = useState("");
  const [compB, setCompB] = useState("");
  const [compLoading, setCompLoading] = useState(false);
  const [compResult, setCompResult] = useState(null);

  const runCompare = async () => {
    if (!compA || !compB || compA === compB) { toast.error("Escolha 2 processos diferentes"); return; }
    setCompLoading(true); setCompResult(null);
    try {
      const r = await axios.post(`${API}/api/mineral/orders/${data.order_id}/compare`,
        { processo_a: compA, processo_b: compB });
      setCompResult(r.data.comparison);
      toast.success(r.data.cached ? "Comparação carregada do cache" : "Comparação gerada!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na comparação");
    } finally { setCompLoading(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="relatorio-loading">
      Carregando relatório…
    </div>
  );
  if (error) return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="relatorio-error"
         style={{ background: "linear-gradient(180deg,#0B1B2F,#050810)" }}>
      <div className="max-w-md text-center bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
        <div className="text-rose-300 mb-3">{error}</div>
        <Link to="/consulta-mineral" className="text-xs text-white/60 hover:text-white">Fazer nova consulta</Link>
      </div>
    </div>
  );

  const payload = data?.payload || {};
  const dp = payload.dados_processo || {};
  const luna = payload.luna_analysis;
  const dadosLote = payload.dados_lote;
  const comparisons = payload.comparisons || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="relatorio-mineral-publico">
      {/* Header dark navy */}
      <div style={{ background: "linear-gradient(120deg, #050810 0%, #0A1730 60%, #0B1B2F 100%)" }}>
        <div className="max-w-4xl mx-auto px-6 py-8">
          <Link to="/consulta-mineral" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-xs">
            <ArrowLeft className="w-3.5 h-3.5"/> Fazer nova consulta
          </Link>
          <div className="mt-4 flex items-center gap-2 mb-2">
            <Mountain className="w-4 h-4" style={{ color: "#F0D37A" }}/>
            <span className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "#F0D37A" }}>
              Hermes Mineral · Relatório Oficial
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl text-white font-semibold">
            {dp.processo_ds || dp.processo_raw || data.order_id}
          </h1>
          {dp.titular && (
            <div className="text-neutral-300/80 mt-2 text-sm">{dp.titular}</div>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-medium"
                 style={{ background: "rgba(212,175,55,.15)", color: "#F0D37A" }}>
              <Clock className="w-3 h-3"/>
              Disponível para consulta até <strong className="ml-1">{formatExpiry(data.expires_at)}</strong>
            </div>
            {!shared && (
              <button onClick={createShare} disabled={creatingShare} data-testid="btn-share-report"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[11px] font-semibold border transition hover:opacity-90"
                style={{ borderColor: "rgba(240, 211, 122, .35)", color: "#F0D37A" }}>
                <Share2 className="w-3 h-3"/> {creatingShare ? "Gerando…" : "Compartilhar com o cliente"}
              </button>
            )}
          </div>
        </div>
      </div>

      {shareOpen && shareUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur"
             data-testid="share-modal"
             onClick={() => setShareOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}
               className="max-w-lg w-full bg-white rounded-2xl p-6 shadow-2xl">
            <div className="flex items-center gap-2 mb-2">
              <Share2 className="w-4 h-4" style={{ color: "#D4AF37" }}/>
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Link temporário</div>
            </div>
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1">Compartilhar com seu cliente</h3>
            <p className="text-sm text-slate-500 mb-4">
              Este link é seguro e temporário — expira em {formatExpiry(shareExp)}. Não expõe seu acesso original.
            </p>
            <div className="flex items-stretch gap-2">
              <input readOnly value={shareUrl} data-testid="share-url-input"
                className="flex-1 h-11 px-3 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 text-sm"/>
              <button onClick={copyShare} data-testid="btn-copy-share"
                className="h-11 px-4 rounded-lg font-semibold text-sm inline-flex items-center gap-1.5"
                style={{ background: "linear-gradient(180deg,#F5DE8C,#D4AF37)", color: "#0B1B2F" }}>
                {copied ? <><Check className="w-4 h-4"/> Copiado</> : <><Copy className="w-4 h-4"/> Copiar</>}
              </button>
            </div>
            <button onClick={() => setShareOpen(false)}
              className="mt-4 text-xs text-slate-400 hover:text-slate-700">Fechar</button>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-6 py-8">
        <Section title="Dados oficiais (SIGMINE / ANM)" testid="section-dados">
          {Object.keys(dp).length === 0 ? (
            <div className="text-slate-500 italic">Informação não localizada nas fontes consultadas.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              <Row k="Nº do processo" v={dp.processo_ds || dp.processo_raw}/>
              <Row k="Titular" v={dp.titular}/>
              <Row k="Fase" v={dp.fase}/>
              <Row k="Último evento" v={dp.ultimo_evento}/>
              <Row k="Substância" v={dp.substancia}/>
              <Row k="Uso" v={dp.uso}/>
              <Row k="Área (ha)" v={dp.area_ha}/>
              <Row k="UF" v={dp.uf}/>
              <Row k="Fonte" v={dp.source}/>
              <Row k="Data do snapshot" v={dp.updated_at && new Date(dp.updated_at).toLocaleDateString("pt-BR")}/>
            </div>
          )}
        </Section>

        <Section title="Análise jurídica LUNA" testid="section-luna">
          <AnalysisRender analysis={luna}/>
        </Section>

        {/* Lote: botão comparar 2 processos */}
        {dadosLote && dadosLote.length >= 2 && !shared && (
          <div className="rounded-2xl border border-amber-200/60 p-5 mb-4 shadow-sm"
               style={{ background: "linear-gradient(90deg,#FFFBEA,#FFF7D6)" }}
               data-testid="section-compare-cta">
            <div className="flex flex-wrap items-center gap-4 justify-between">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "#B45309" }}>
                  Comparar processos
                </div>
                <div className="font-heading text-lg font-semibold text-slate-900 mt-1">
                  Gerar comparação avulsa entre 2 processos deste lote
                </div>
                <div className="text-xs text-slate-600 mt-1">
                  Sem cobrança extra — a comparação é reaproveitada do processamento pago.
                </div>
              </div>
              <button onClick={() => setCompareOpen(true)} data-testid="btn-compare"
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl font-semibold text-sm shadow-sm"
                style={{ background: "#0B1B2F", color: "#F0D37A" }}>
                <GitCompare className="w-4 h-4"/> Nova comparação
              </button>
            </div>
          </div>
        )}

        {/* Comparações já geradas */}
        {comparisons.length > 0 && (
          <Section title={`${comparisons.length} comparação${comparisons.length === 1 ? "" : "ões"} gerada${comparisons.length === 1 ? "" : "s"}`}
                   testid="section-comparisons">
            <div className="space-y-4">
              {comparisons.map((c, i) => (
                <div key={i} className="p-4 rounded-xl border border-slate-200 bg-slate-50" data-testid={`comparison-${i}`}>
                  <div className="text-xs text-slate-500 mb-2">
                    <strong className="text-slate-900">{c.processo_a}</strong> ⇔ <strong className="text-slate-900">{c.processo_b}</strong>
                  </div>
                  <AnalysisRender analysis={c.analysis}/>
                </div>
              ))}
            </div>
          </Section>
        )}

        {compareOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur"
               data-testid="compare-modal"
               onClick={() => setCompareOpen(false)}>
            <div onClick={(e) => e.stopPropagation()}
                 className="max-w-2xl w-full bg-white rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center gap-2 mb-3">
                <GitCompare className="w-4 h-4" style={{ color: "#D4AF37" }}/>
                <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
                  Comparar processos do lote
                </div>
              </div>
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">
                Escolha 2 processos para comparar
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Processo A</label>
                  <select value={compA} onChange={(e) => setCompA(e.target.value)}
                    data-testid="select-compare-a"
                    className="w-full h-11 px-3 rounded-lg border border-slate-200 mt-1 text-sm">
                    <option value="">Selecione…</option>
                    {dadosLote?.map((d, i) => (
                      <option key={i} value={d.query}>{d.query}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Processo B</label>
                  <select value={compB} onChange={(e) => setCompB(e.target.value)}
                    data-testid="select-compare-b"
                    className="w-full h-11 px-3 rounded-lg border border-slate-200 mt-1 text-sm">
                    <option value="">Selecione…</option>
                    {dadosLote?.map((d, i) => (
                      <option key={i} value={d.query}>{d.query}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button onClick={runCompare} disabled={compLoading}
                data-testid="btn-run-compare"
                className="mt-4 w-full h-11 rounded-lg font-semibold text-sm shadow-sm disabled:opacity-60"
                style={{ background: "linear-gradient(180deg,#F5DE8C,#D4AF37)", color: "#0B1B2F" }}>
                {compLoading ? "Comparando…" : "Gerar comparação"}
              </button>
              {compResult && (
                <div className="mt-4 p-3 rounded-lg bg-slate-50 border border-slate-200 max-h-72 overflow-y-auto" data-testid="compare-result">
                  <div className="text-xs text-slate-500 mb-2">
                    <strong>{compResult.processo_a}</strong> ⇔ <strong>{compResult.processo_b}</strong>
                  </div>
                  <AnalysisRender analysis={compResult.analysis}/>
                </div>
              )}
              <button onClick={() => { setCompareOpen(false); setCompResult(null); }}
                className="mt-3 text-xs text-slate-400 hover:text-slate-700">Fechar</button>
            </div>
          </div>
        )}

        <div className="text-center text-xs text-slate-400 mt-8">
          Pedido {data.order_id} · Fonte pública ANM · Análise assistida por IA jurídica LUNA (não substitui aconselhamento profissional)
        </div>
      </div>
    </div>
  );
}
